import { useEffect, useMemo, useState } from "react";
import { dashboardApi, phase1Api } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import PortalSidebar from "../components/PortalSidebar";
import PortalTopbar from "../components/PortalTopbar";

function numberValue(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function statusTone(value) {
  const status = String(value || "").toUpperCase();
  if (["APPROVED", "PUBLISHED", "ACTIVE", "CONFIRMED", "COMPLETED", "DELIVERED"].includes(status)) return "good";
  if (["PENDING", "PENDING_SELLER", "UNDER_REVIEW", "REQUESTED", "PROCESSING", "READY_FOR_DELIVERY", "IN_TRANSIT"].includes(status)) return "warn";
  if (["REJECTED", "SUSPENDED", "CANCELLED", "FAILED"].includes(status)) return "bad";
  return "neutral";
}

function getWorkspace(user) {
  if (!user) return "BUYER";
  if (user.is_platform_staff || user.is_superuser || ["SUPER_ADMIN", "ADMIN", "SUPPORT", "FINANCE", "OPERATIONS"].includes(user.account_type)) return "ADMIN";
  if (user.account_type === "BUSINESS") return "BUSINESS";
  if (user.account_type === "SELLER") return "SELLER";
  if (user.account_type === "COURIER") return "COURIER";
  return "BUYER";
}

function MetricCard({ label, value, note }) {
  return <article><span>{label}</span><strong>{value}</strong><small>{note}</small></article>;
}

function MiniPipeline({ title, rows }) {
  return (
    <article className="role-panel-card role-panel-card--wide">
      <div className="card-title-row compact"><div><span>WORKFLOW</span><h3>{title}</h3></div></div>
      <div className="role-pipeline-list">
        {rows.length ? rows.slice(0, 6).map((row, index) => (
          <div key={row.id || row.order_number || row.code || index}>
            <div><strong>{row.order_number || row.code || row.product_title || row.title || "Record"}</strong><span>{row.product_title || row.buyer_name || row.seller_name || row.dropoff_address || row.message || "Latest activity"}</span></div>
            <span className={`status-chip ${statusTone(row.status || row.payment_status)}`}>{row.status || row.payment_status || "ACTIVE"}</span>
          </div>
        )) : <div className="records-empty soft-empty">No activity yet.</div>}
      </div>
    </article>
  );
}

export default function PortalDashboard() {
  const { user, logout } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [summary, setSummary] = useState(null);
  const [workspaceData, setWorkspaceData] = useState({ products: [], enquiries: [], orders: [], deliveries: [], documents: [], couriers: [], services: [] });
  const [error, setError] = useState("");

  const workspace = getWorkspace(user);
  const displayName = user?.display_name || user?.full_name || user?.email || "TAZY user";

  async function load() {
    setError("");
    try {
      const summaryRes = await dashboardApi.summary();
      setSummary(summaryRes.data);
    } catch {
      setSummary(null);
    }

    try {
      if (workspace === "ADMIN") {
        const [enquiries, orders, deliveries, documents] = await Promise.all([phase1Api.enquiries(), phase1Api.orders(), phase1Api.deliveries(), phase1Api.documents()]);
        setWorkspaceData({ enquiries: enquiries.data || [], orders: orders.data || [], deliveries: deliveries.data || [], documents: documents.data || [], products: [], couriers: [], services: [] });
      } else if (workspace === "SELLER") {
        const [store, enquiries, orders, documents] = await Promise.all([phase1Api.sellerWorkspace(), phase1Api.enquiries(), phase1Api.orders(), phase1Api.documents()]);
        setWorkspaceData({ products: store.data?.products || [], enquiries: enquiries.data || [], orders: orders.data || [], documents: documents.data || [], deliveries: [], couriers: [], services: [] });
      } else if (workspace === "BUSINESS") {
        const [business, enquiries, orders, documents] = await Promise.all([phase1Api.businessWorkspace(), phase1Api.enquiries(), phase1Api.orders(), phase1Api.documents()]);
        setWorkspaceData({ products: business.data?.products || [], enquiries: enquiries.data || [], orders: orders.data || [], documents: documents.data || [], deliveries: business.data?.goods_movements || [], couriers: [], services: [] });
      } else if (workspace === "COURIER") {
        const [couriers, services, deliveries, documents] = await Promise.all([phase1Api.couriers(), phase1Api.transporterServices(), phase1Api.deliveries(), phase1Api.documents()]);
        setWorkspaceData({ couriers: couriers.data || [], services: services.data || [], deliveries: deliveries.data || [], documents: documents.data || [], products: [], enquiries: [], orders: [] });
      } else {
        const [enquiries, orders] = await Promise.all([phase1Api.enquiries(), phase1Api.orders()]);
        setWorkspaceData({ enquiries: enquiries.data || [], orders: orders.data || [], products: [], deliveries: [], documents: [], couriers: [], services: [] });
      }
    } catch {
      setError("Unable to load all workspace records. Please refresh shortly.");
    }
  }

  useEffect(() => { load(); }, [workspace]);

  const counts = summary?.counts || {};
  const roleConfig = useMemo(() => {
    if (workspace === "ADMIN") {
      return {
        eyebrow: "PLATFORM OPERATIONS",
        title: "Marketplace control centre",
        text: "Monitor sellers, buyers, transporters, documents, enquiries, orders and fulfilment movement across the platform.",
        metrics: [
          ["Total users", numberValue(counts.total_users), "All account lanes"],
          ["Verified sellers", numberValue(counts.sellers), "Seller and business accounts"],
          ["Transporters", numberValue(counts.couriers), "Courier network"],
          ["Pending documents", numberValue(counts.documents_pending), "Requires review"],
        ],
        pipelineTitle: "Operations queue",
        pipelineRows: [...workspaceData.documents, ...workspaceData.enquiries, ...workspaceData.orders],
      };
    }
    if (workspace === "SELLER") {
      return {
        eyebrow: "MERCHANT WORKSPACE",
        title: `Welcome back, ${displayName}`,
        text: "Manage store readiness, products, enquiries, orders and fulfilment handover from one seller workspace.",
        metrics: [
          ["Products", workspaceData.products.length, "Catalogue items"],
          ["Enquiries", workspaceData.enquiries.length, "Buyer requests"],
          ["Orders", workspaceData.orders.length, "Linked to this store"],
          ["Documents", workspaceData.documents.length, "Verification files"],
        ],
        pipelineTitle: "Seller action queue",
        pipelineRows: [...workspaceData.enquiries, ...workspaceData.orders],
      };
    }
    if (workspace === "BUSINESS") {
      return {
        eyebrow: "BUSINESS OPERATIONS",
        title: `Business workspace for ${displayName}`,
        text: "Control catalogue operations, branches, suppliers, inventory movement, orders and delivery readiness.",
        metrics: [
          ["Products", workspaceData.products.length, "Business catalogue"],
          ["Orders", workspaceData.orders.length, "Customer orders"],
          ["Goods movement", workspaceData.deliveries.length, "Inventory records"],
          ["Documents", workspaceData.documents.length, "Compliance files"],
        ],
        pipelineTitle: "Business fulfilment queue",
        pipelineRows: [...workspaceData.orders, ...workspaceData.deliveries],
      };
    }
    if (workspace === "COURIER") {
      return {
        eyebrow: "TRANSPORTER WORKSPACE",
        title: `Transporter dashboard for ${displayName}`,
        text: "Complete transporter profile, upload verification documents, manage services and progress delivery requests.",
        metrics: [
          ["Profiles", workspaceData.couriers.length, "Transporter record"],
          ["Services", workspaceData.services.length, "Delivery offerings"],
          ["Delivery requests", workspaceData.deliveries.length, "Assigned jobs"],
          ["Documents", workspaceData.documents.length, "Verification files"],
        ],
        pipelineTitle: "Delivery queue",
        pipelineRows: workspaceData.deliveries,
      };
    }
    return {
      eyebrow: "BUYER WORKSPACE",
      title: `Buyer workspace for ${displayName}`,
      text: "Track enquiries, orders, seller responses and fulfilment progress after sourcing products from the marketplace.",
      metrics: [
        ["Enquiries", workspaceData.enquiries.length, "Sent to sellers"],
        ["Orders", workspaceData.orders.length, "Placed from marketplace"],
        ["Active", workspaceData.orders.filter((item) => !["COMPLETED", "CANCELLED"].includes(item.status)).length, "In progress"],
        ["Completed", workspaceData.orders.filter((item) => item.status === "COMPLETED").length, "Finished"],
      ],
      pipelineTitle: "My buying journey",
      pipelineRows: [...workspaceData.orders, ...workspaceData.enquiries],
    };
  }, [workspace, workspaceData, counts, displayName]);

  const totalForDonut = Math.max(1, roleConfig.metrics.reduce((sum, item) => sum + numberValue(item[1]), 0));
  const firstShare = Math.round((numberValue(roleConfig.metrics[0]?.[1]) / totalForDonut) * 100);
  const secondShare = Math.round((numberValue(roleConfig.metrics[1]?.[1]) / totalForDonut) * 100);

  return (
    <div className={`portal-shell-v2 refined-portal-shell ${sidebarCollapsed ? "is-collapsed" : ""}`}>
      <PortalSidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed((value) => !value)} />
      <main className="portal-main-v2 role-dashboard-main">
        <PortalTopbar user={user} onLogout={logout} />
        {error && <div className="portal-warning">{error}</div>}

        <section className="portal-hero-panel role-hero-panel" id="overview">
          <div>
            <span>{roleConfig.eyebrow}</span>
            <h2>{roleConfig.title}</h2>
            <p>{roleConfig.text}</p>
          </div>
          <div className="hero-score-card">
            <small>Workspace readiness</small>
            <strong>{workspace === "ADMIN" ? "86%" : workspace === "COURIER" ? "62%" : "74%"}</strong>
            <span>{workspace === "ADMIN" ? "Foundation online" : "Continue setup"}</span>
          </div>
        </section>

        <section className="portal-kpi-strip role-kpi-strip">
          {roleConfig.metrics.map(([label, value, note]) => <MetricCard key={label} label={label} value={value} note={note} />)}
        </section>

        <section className="analytics-grid-v2 role-analytics-grid">
          <MiniPipeline title={roleConfig.pipelineTitle} rows={roleConfig.pipelineRows} />
          <article className="analytics-card role-panel-card">
            <div className="card-title-row compact"><div><span>COMPOSITION</span><h3>Workspace mix</h3></div></div>
            <div className="donut-wrap">
              <div className="donut-chart" style={{ background: `conic-gradient(#ff6500 0 ${firstShare}%, #071b3d ${firstShare}% ${firstShare + secondShare}%, #37b26c ${firstShare + secondShare}% 100%)` }}>
                <div><strong>{totalForDonut}</strong><span>Records</span></div>
              </div>
              <div className="donut-legend">
                {roleConfig.metrics.slice(0, 3).map(([label, value], index) => <span key={label}><i className={index === 0 ? "orange" : index === 1 ? "navy" : "green"} /> {label} {value}</span>)}
              </div>
            </div>
          </article>
          <article className="analytics-card role-panel-card role-next-actions">
            <div className="card-title-row compact"><div><span>NEXT ACTIONS</span><h3>What to do next</h3></div></div>
            <ul>
              {workspace === "COURIER" ? (
                <>
                  <li>Complete transporter profile and contact details.</li>
                  <li>Upload National ID, vehicle registration and driver licence.</li>
                  <li>Add delivery services and service areas.</li>
                  <li>Open Delivery Requests when orders are assigned.</li>
                </>
              ) : workspace === "SELLER" ? (
                <>
                  <li>Complete your store profile and platform enquiry settings.</li>
                  <li>Upload required verification documents.</li>
                  <li>Add products and keep stock status accurate.</li>
                  <li>Respond to buyer enquiries before orders stall.</li>
                </>
              ) : workspace === "BUSINESS" ? (
                <>
                  <li>Add branches and suppliers before recording movement.</li>
                  <li>Keep product availability public but internal quantity private.</li>
                  <li>Use goods movement for receiving, transfer, dispatch and defective batches.</li>
                  <li>Progress orders into fulfilment when stock is ready.</li>
                </>
              ) : workspace === "BUYER" ? (
                <>
                  <li>Search the marketplace and open product details.</li>
                  <li>Place order directly or ask seller first.</li>
                  <li>Track seller confirmation and delivery progress.</li>
                </>
              ) : (
                <>
                  <li>Review seller and transporter documents before approval.</li>
                  <li>Manage category and subcategory quality.</li>
                  <li>Monitor orders and fulfilment exceptions.</li>
                </>
              )}
            </ul>
          </article>
        </section>
      </main>
    </div>
  );
}
